import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import emailjs from "@emailjs/browser";
import "./OrderConfirmPage.css";
import { getCartUserId } from "../utils/cartUser";
import {
  getLocalCartItems,
  isLocalCartItemId,
  removeItemFromLocalCart,
} from "../utils/localCart";

const parsePrice = (value) =>
  parseFloat(String(value ?? "").replace(/[^0-9.]/g, "")) || 0;

const BACKEND_BASE_URL =
  process.env.REACT_APP_BACKEND_BASE_URL || "https://gp-live-backend.vercel.app";
const EMAILJS_SERVICE_ID =
  process.env.REACT_APP_EMAILJS_SERVICE_ID || "service_sva2qhq";
const EMAILJS_ORDER_ADMIN_TEMPLATE_ID =
  process.env.REACT_APP_EMAILJS_ORDER_ADMIN_TEMPLATE_ID ||
  process.env.REACT_APP_EMAILJS_ADMIN_TEMPLATE_ID ||
  "template_qq1gspb";
const EMAILJS_ORDER_CUSTOMER_TEMPLATE_ID =
  process.env.REACT_APP_EMAILJS_ORDER_CUSTOMER_TEMPLATE_ID ||
  process.env.REACT_APP_EMAILJS_USER_TEMPLATE_ID ||
  "template_r2b5iz6";
const EMAILJS_PUBLIC_KEY =
  process.env.REACT_APP_EMAILJS_PUBLIC_KEY || "lecC-PMsmD6UhX_fA";
const ORDER_ADMIN_EMAIL =
  process.env.REACT_APP_ORDER_ADMIN_EMAIL || "info.glamurpaints@gmail.com";
const ORDER_ADMIN_EMAILS = (
  process.env.REACT_APP_ORDER_ADMIN_EMAILS || ORDER_ADMIN_EMAIL
)
  .split(",")
  .map((email) => email.trim())
  .filter(Boolean);
const DEFAULT_LOGO_URL = "https://www.glamurpaints.com/assets/logo2.png";
const COMPANY_NAME = process.env.REACT_APP_COMPANY_NAME || "Glamur Paints";
const COMPANY_LOGO_URL =
  (process.env.REACT_APP_COMPANY_LOGO_URL || DEFAULT_LOGO_URL).trim();
const COMPANY_PHONE = process.env.REACT_APP_COMPANY_PHONE || "+92 314 7864360";
const COMPANY_WEBSITE = process.env.REACT_APP_COMPANY_WEBSITE || "";
const PUBLIC_SITE_URL = (
  process.env.REACT_APP_PUBLIC_SITE_URL || "https://www.glamurpaints.com"
).replace(/\/+$/, "");
const ORDER_COUNTER_STORAGE_KEY = "gp_order_counter";
const ORDER_COUNTER_START = 1001;
const ORDER_SEQUENCE_USER_ID =
  process.env.REACT_APP_ORDER_SEQUENCE_USER_ID || "__gp_order_sequence__";
const ORDER_SEQUENCE_NAME_PREFIX = "__GP_ORDER_SEQUENCE__";
const ORDER_SEQUENCE_FETCH_RETRIES = 3;
const ORDER_SEQUENCE_FETCH_RETRY_DELAY_MS = 250;

const escapeHtml = (value = "") =>
  String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");

const normalizeImageUrl = (value) => {
  const src = String(value || "").trim();
  if (!src) return "";
  if (/^https?:\/\//i.test(src)) return src;
  if (src.startsWith("//")) return `https:${src}`;
  const normalizedPath = src.startsWith("/") ? src : `/${src}`;
  return `${PUBLIC_SITE_URL}${normalizedPath}`;
};

const normalizeCartResponse = (data) => {
  if (Array.isArray(data)) return data;
  if (data && typeof data === "object") return [data];
  return [];
};

const getNextLocalOrderNumber = () => {
  try {
    const rawValue = localStorage.getItem(ORDER_COUNTER_STORAGE_KEY);
    const parsedValue = Number.parseInt(rawValue || "", 10);
    const currentValue =
      Number.isFinite(parsedValue) && parsedValue >= ORDER_COUNTER_START
        ? parsedValue
        : ORDER_COUNTER_START;

    localStorage.setItem(ORDER_COUNTER_STORAGE_KEY, String(currentValue + 1));
    return `GP-${currentValue}`;
  } catch {
    // Fallback in case localStorage is unavailable.
    return `GP-${ORDER_COUNTER_START}`;
  }
};

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const getOrderSequenceTimestamp = (item) => {
  const timestamp = Date.parse(item?.dateAdded || "");
  return Number.isFinite(timestamp) ? timestamp : 0;
};

const sortSequenceItems = (items) =>
  [...items].sort((firstItem, secondItem) => {
    const dateDiff =
      getOrderSequenceTimestamp(firstItem) - getOrderSequenceTimestamp(secondItem);
    if (dateDiff !== 0) return dateDiff;

    const firstId = String(firstItem?._id || "");
    const secondId = String(secondItem?._id || "");
    return firstId.localeCompare(secondId);
  });

const reserveNextOrderNumber = async () => {
  const markerPayload = {
    userId: ORDER_SEQUENCE_USER_ID,
    name: `${ORDER_SEQUENCE_NAME_PREFIX}-${Date.now()}`,
    price: 0,
    img: "",
    quantity: 1,
    size: "N/A",
    color: {
      name: "N/A",
      hex: "#000000",
    },
  };

  const createMarkerResponse = await fetch(`${BACKEND_BASE_URL}/api/cart`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(markerPayload),
  });

  if (!createMarkerResponse.ok) {
    throw new Error(
      `Failed to reserve order marker. HTTP ${createMarkerResponse.status}`
    );
  }

  const createdMarker = await createMarkerResponse.json();
  const createdMarkerId = createdMarker?._id;

  if (!createdMarkerId) {
    throw new Error("Failed to reserve order marker id.");
  }

  for (let attempt = 0; attempt < ORDER_SEQUENCE_FETCH_RETRIES; attempt += 1) {
    const sequenceResponse = await fetch(
      `${BACKEND_BASE_URL}/api/cart/${ORDER_SEQUENCE_USER_ID}`
    );

    if (!sequenceResponse.ok) {
      throw new Error(
        `Failed to load order sequence. HTTP ${sequenceResponse.status}`
      );
    }

    const sequencePayload = await sequenceResponse.json();
    const sequenceItems = sortSequenceItems(
      normalizeCartResponse(sequencePayload).filter((item) => item?._id)
    );
    const markerIndex = sequenceItems.findIndex(
      (item) => item._id === createdMarkerId
    );

    if (markerIndex >= 0) {
      return `GP-${ORDER_COUNTER_START + markerIndex}`;
    }

    if (attempt < ORDER_SEQUENCE_FETCH_RETRIES - 1) {
      await sleep(ORDER_SEQUENCE_FETCH_RETRY_DELAY_MS);
    }
  }

  throw new Error("Order sequence marker was created but not indexed.");
};

export default function OrderConfirmPage() {
  const navigate = useNavigate();
  const userId = getCartUserId();

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [orderForm, setOrderForm] = useState({
    customerName: "",
    contactNumber: "",
    address: "",
    email: "",
    whatsappNumber: "",
  });

  const fetchCart = useCallback(() => {
    setLoading(true);
    const localItems = getLocalCartItems(userId);

    fetch(`${BACKEND_BASE_URL}/api/cart/${userId}`)
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .then((data) => {
        const serverItems = Array.isArray(data) ? data : [];
        setItems([...serverItems, ...localItems]);
        setError("");
      })
      .catch(() => {
        setItems(localItems);
        setError(localItems.length === 0 ? "Failed to load cart items." : "");
      })
      .finally(() => setLoading(false));
  }, [userId]);

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchCart();
  }, [fetchCart]);

  const total = useMemo(
    () => items.reduce((sum, item) => sum + parsePrice(item.price) * (item.quantity || 1), 0),
    [items]
  );

  const clearCartAfterOrder = useCallback(async () => {
    const localItems = items.filter((item) => isLocalCartItemId(item?._id));
    const serverItems = items.filter((item) => !isLocalCartItemId(item?._id));

    localItems.forEach((item) => removeItemFromLocalCart(userId, item._id));

    await Promise.allSettled(
      serverItems
        .filter((item) => item?._id)
        .map((item) =>
          fetch(
            `${BACKEND_BASE_URL}/api/cart/${userId}/${item._id}`,
            { method: "DELETE" }
          )
        )
    );

    window.dispatchEvent(new Event("cartUpdated"));
  }, [items, userId]);

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setOrderForm((prev) => ({ ...prev, [name]: value }));
    setFormErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const validateForm = () => {
    const nextErrors = {};
    const cleanEmail = orderForm.email.trim();
    const contactDigits = orderForm.contactNumber.replace(/\D/g, "");
    const whatsappDigits = orderForm.whatsappNumber.replace(/\D/g, "");

    if (!orderForm.customerName.trim()) {
      nextErrors.customerName = "Name is required.";
    }
    if (!orderForm.contactNumber.trim()) {
      nextErrors.contactNumber = "Contact number is required.";
    } else if (contactDigits.length < 10) {
      nextErrors.contactNumber = "Enter a valid contact number.";
    }
    if (!orderForm.address.trim()) {
      nextErrors.address = "Address is required.";
    }
    if (!cleanEmail) {
      nextErrors.email = "Email is required.";
    } else if (!/^\S+@\S+\.\S+$/.test(cleanEmail)) {
      nextErrors.email = "Enter a valid email address.";
    }
    if (!orderForm.whatsappNumber.trim()) {
      nextErrors.whatsappNumber = "WhatsApp number is required.";
    } else if (whatsappDigits.length < 10) {
      nextErrors.whatsappNumber = "Enter a valid WhatsApp number.";
    }

    setFormErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const buildProductsText = () =>
    items
      .map((item, index) => {
        const quantity = item.quantity || 1;
        const unitPrice = parsePrice(item.price);
        const subtotal = unitPrice * quantity;
        return [
          `${index + 1}. ${item.name || "Product"}`,
          `Size: ${item.size || "N/A"}`,
          `Color: ${item.color?.name || "N/A"} (${item.color?.hex || "-"})`,
          `Quantity: ${quantity}`,
          `Unit Price: Rs ${unitPrice.toFixed(2)}`,
          `Subtotal: Rs ${subtotal.toFixed(2)}`,
        ].join("\n");
      })
      .join("\n\n");

  const buildProductsHtmlWithImages = () =>
    items
      .map((item, index) => {
        const quantity = item.quantity || 1;
        const unitPrice = parsePrice(item.price);
        const subtotal = unitPrice * quantity;
        const imageUrl = normalizeImageUrl(item.img) || DEFAULT_LOGO_URL;
        const productName = item.name || "Product";
        const size = item.size || "N/A";
        const colorName = item.color?.name || "N/A";
        const colorHex = item.color?.hex || "-";

        return `
<table role="presentation" width="100%" cellspacing="0" cellpadding="0" style="margin:0 0 10px 0;border:1px solid #e5e7eb;border-radius:10px;background:#ffffff;">
  <tr>
    <td width="92" style="padding:10px;vertical-align:top;">
      <img src="${escapeHtml(imageUrl)}" alt="${escapeHtml(
          productName
        )}" width="80" style="display:block;width:80px;height:80px;object-fit:cover;border-radius:8px;border:1px solid #e5e7eb;background:#ffffff;" />
    </td>
    <td style="padding:10px 10px 10px 0;vertical-align:top;color:#111827;font-family:Arial,sans-serif;font-size:13px;line-height:1.5;">
      <div style="font-size:14px;font-weight:700;margin-bottom:4px;">${index + 1}. ${escapeHtml(
          productName
        )}</div>
      <div><strong>Size:</strong> ${escapeHtml(size)}</div>
      <div><strong>Color:</strong> ${escapeHtml(colorName)} (${escapeHtml(colorHex)})</div>
      <div><strong>Qty:</strong> ${quantity}</div>
      <div><strong>Unit Price:</strong> Rs ${unitPrice.toFixed(2)}</div>
      <div><strong>Subtotal:</strong> Rs ${subtotal.toFixed(2)}</div>
    </td>
  </tr>
</table>`;
      })
      .join("");

  const handleConfirmOrder = async (event) => {
    event.preventDefault();

    if (items.length === 0) {
      alert("Cart is empty.");
      return;
    }

    if (!validateForm()) return;

    try {
      setSubmitting(true);

      let orderNumber = "";
      try {
        orderNumber = await reserveNextOrderNumber();
      } catch (sequenceError) {
        console.error(
          "Order number sequence API failed, falling back to local counter.",
          sequenceError
        );
        orderNumber = getNextLocalOrderNumber();
      }
      const orderDate = new Date().toLocaleString("en-PK", {
        dateStyle: "medium",
        timeStyle: "short",
      });
      const productsText = buildProductsText();
      const productsHtml = buildProductsHtmlWithImages();
      const productsTextHtml = escapeHtml(productsText).replace(/\n/g, "<br/>");

      const adminMessage = [
        `New order received (${orderNumber})`,
        `Date: ${orderDate}`,
        "",
        "Customer Details:",
        `Name: ${orderForm.customerName.trim()}`,
        `Email: ${orderForm.email.trim()}`,
        `Contact: ${orderForm.contactNumber.trim()}`,
        `WhatsApp: ${orderForm.whatsappNumber.trim()}`,
        `Address: ${orderForm.address.trim()}`,
        "",
        "Order Items:",
        productsText,
        "",
        `Total: Rs ${total.toFixed(2)}`,
      ].join("\n");

      const customerMessage = [
        `Assalam o Alaikum ${orderForm.customerName.trim()},`,
        "",
        `Thank you for your order. Your order number is ${orderNumber}.`,
        `Date: ${orderDate}`,
        "",
        "Order Summary:",
        productsText,
        "",
        `Total: Rs ${total.toFixed(2)}`,
        "",
        "Our team will contact you soon for processing and delivery.",
      ].join("\n");

      const commonTemplateParams = {
        order_number: orderNumber,
        order_date: orderDate,
        customer_name: orderForm.customerName.trim(),
        to_name: orderForm.customerName.trim(),
        customer_email: orderForm.email.trim(),
        customer_phone: orderForm.contactNumber.trim(),
        customer_whatsapp: orderForm.whatsappNumber.trim(),
        customer_address: orderForm.address.trim(),
        total_amount: total.toFixed(2),
        order_items: productsText,
        order_items_html: productsHtml,
        order_items_text_html: productsTextHtml,
        firstName: orderForm.customerName.trim(),
        lastName: "",
        email: orderForm.email.trim(),
        phone: orderForm.contactNumber.trim(),
        from_name: COMPANY_NAME,
        from_email: ORDER_ADMIN_EMAIL,
        admin_email: ORDER_ADMIN_EMAIL,
        reply_to: orderForm.email.trim(),
        company_name: COMPANY_NAME,
        company_logo_url: COMPANY_LOGO_URL,
        logo_url: COMPANY_LOGO_URL,
        company_phone: COMPANY_PHONE,
        company_email: ORDER_ADMIN_EMAIL,
        company_website: COMPANY_WEBSITE,
        website_url: COMPANY_WEBSITE,
      };

      const adminTemplateParams = {
        ...commonTemplateParams,
        to_email: ORDER_ADMIN_EMAIL,
        to: ORDER_ADMIN_EMAIL,
        recipient_email: ORDER_ADMIN_EMAIL,
        user_email: ORDER_ADMIN_EMAIL,
        message: adminMessage,
      };

      const customerTemplateParams = {
        ...commonTemplateParams,
        to_email: orderForm.email.trim(),
        message: customerMessage,
      };

      // Step 1: Send to admin inbox(es) first so no order is confirmed without admin notification.
      const adminResults = await Promise.allSettled(
        ORDER_ADMIN_EMAILS.map((adminEmail) =>
          emailjs.send(
            EMAILJS_SERVICE_ID,
            EMAILJS_ORDER_ADMIN_TEMPLATE_ID,
            {
              ...adminTemplateParams,
              to_email: adminEmail,
              to: adminEmail,
              recipient_email: adminEmail,
              user_email: adminEmail,
              admin_email: adminEmail,
              company_email: adminEmail,
              from_email: adminEmail,
            },
            EMAILJS_PUBLIC_KEY
          )
        )
      );

      const successfulAdminSends = adminResults.filter(
        (result) => result.status === "fulfilled"
      ).length;

      if (successfulAdminSends === 0) {
        console.error("Admin email failed for all recipients:", {
          recipients: ORDER_ADMIN_EMAILS,
          adminResults,
        });
        throw new Error("Admin email delivery failed.");
      }

      // Step 2: Admin succeeded, now send customer confirmation.
      await emailjs.send(
        EMAILJS_SERVICE_ID,
        EMAILJS_ORDER_CUSTOMER_TEMPLATE_ID,
        customerTemplateParams,
        EMAILJS_PUBLIC_KEY
      );

      await clearCartAfterOrder();
      navigate("/success", {
        state: {
          orderNumber,
          customerName: orderForm.customerName.trim(),
          total: total.toFixed(2),
        },
      });
    } catch (err) {
      console.error("Order confirmation error:", err);
      alert(
        "Order confirm nahi ho saka. Admin ko email send fail hua, please dobara try karein."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="order-confirm-page">
      <div className="order-confirm-container">
        <h2>Order Confirmation</h2>
        <p className="order-confirm-subtitle">
          Please verify your products, fill in your details, and confirm your order.
        </p>

        {loading ? (
          <p>Loading order details...</p>
        ) : items.length === 0 ? (
          <div className="empty-order-box">
            <p>{error || "Aapka cart empty hai."}</p>
            <button className="back-to-cart-btn" onClick={() => navigate("/cart")}>
              Go to Cart
            </button>
          </div>
        ) : (
          <div className="order-confirm-layout">
            <section className="order-summary-card">
              <h3>Products Summary</h3>
              <div className="order-summary-list">
                {items.map((item) => {
                  const quantity = item.quantity || 1;
                  const unitPrice = parsePrice(item.price);
                  const subtotal = unitPrice * quantity;
                  const imageUrl = normalizeImageUrl(item.img) || DEFAULT_LOGO_URL;

                  return (
                    <div key={item._id} className="order-summary-item">
                      <div className="order-summary-item-main">
                        <img
                          src={imageUrl}
                          alt={item.name || "Product"}
                          className="order-summary-image"
                          onError={(event) => {
                            event.currentTarget.src = DEFAULT_LOGO_URL;
                          }}
                        />
                        <div className="order-summary-content">
                          <p>
                            <strong>{item.name || "Product"}</strong>
                          </p>
                          <p>Size: {item.size || "N/A"}</p>
                          <p>
                            Color: {item.color?.name || "N/A"} ({item.color?.hex || "-"})
                          </p>
                          <p>
                            Qty: {quantity} x Rs {unitPrice.toFixed(2)}
                          </p>
                          <p>
                            <strong>Subtotal: Rs {subtotal.toFixed(2)}</strong>
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              <p className="order-grand-total">Total: Rs {total.toFixed(2)}</p>
            </section>

            <section className="customer-details-card">
              <h3>Customer Details</h3>
              <form className="order-form" onSubmit={handleConfirmOrder}>
                <div className="form-field">
                  <label htmlFor="customerName">Name</label>
                  <input
                    id="customerName"
                    name="customerName"
                    type="text"
                    value={orderForm.customerName}
                    onChange={handleInputChange}
                    placeholder="Enter your full name"
                  />
                  {formErrors.customerName && (
                    <p className="field-error">{formErrors.customerName}</p>
                  )}
                </div>

                <div className="form-field">
                  <label htmlFor="contactNumber">Contact Number</label>
                  <input
                    id="contactNumber"
                    name="contactNumber"
                    type="tel"
                    value={orderForm.contactNumber}
                    onChange={handleInputChange}
                    placeholder="03XXXXXXXXX"
                  />
                  {formErrors.contactNumber && (
                    <p className="field-error">{formErrors.contactNumber}</p>
                  )}
                </div>

                <div className="form-field">
                  <label htmlFor="email">Email</label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    value={orderForm.email}
                    onChange={handleInputChange}
                    placeholder="you@example.com"
                  />
                  {formErrors.email && <p className="field-error">{formErrors.email}</p>}
                </div>

                <div className="form-field">
                  <label htmlFor="whatsappNumber">WhatsApp Number</label>
                  <input
                    id="whatsappNumber"
                    name="whatsappNumber"
                    type="tel"
                    value={orderForm.whatsappNumber}
                    onChange={handleInputChange}
                    placeholder="03XXXXXXXXX"
                  />
                  {formErrors.whatsappNumber && (
                    <p className="field-error">{formErrors.whatsappNumber}</p>
                  )}
                </div>

                <div className="form-field">
                  <label htmlFor="address">Address</label>
                  <textarea
                    id="address"
                    name="address"
                    value={orderForm.address}
                    onChange={handleInputChange}
                    rows={4}
                    placeholder="Enter complete address"
                  />
                  {formErrors.address && <p className="field-error">{formErrors.address}</p>}
                </div>

                <div className="order-action-buttons">
                  <button
                    className="secondary-btn"
                    type="button"
                    onClick={() => navigate("/cart")}
                    disabled={submitting}
                  >
                    Back to Cart
                  </button>
                  <button className="primary-btn" type="submit" disabled={submitting}>
                    {submitting ? "Confirming..." : "Confirm Order"}
                  </button>
                </div>
              </form>
            </section>
          </div>
        )}
      </div>
    </div>
  );
}
